import AccessAlarmsIcon from '@mui/icons-material/AccessAlarms';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FilterListIcon from "@mui/icons-material/FilterList";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Avatar,
  Box,
  Button,
  Fade,
  FormControl,
  Grid,
  InputLabel,
  Modal,
  Select,
  MenuItem as SelectMenuItem,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Axios from "axios";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { tokens } from "../../theme";
import ManualModal from "./manualModal";
import MakeupDocModal from "./makeupDocModal";
import MakeupStartTimesModal from "./MakeupStartTimesModal";
import getCookie from "../../utils/getCookies";
/**
 * Makeup Dashboard
 *
 * Status of Makeup Events
 * 0: Makeup Row Created (Event is canceled but teacher email has not been sent yet) --default value
 * 1: Pending Teacher 1차 (Event is canceled and teacher has not selected a window yet)
 * 2: Pending Teacher 2차 (Teacher has requested a new window) --can only request once
 * 3: Pending Parent (Teacher has selected a window and waiting for parent to confirm)
 * 4: Approved (Parent has confirmed the window and the event is created)
 */

export default function MakeupDashboard() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [allEvents, setAllEvents] = useState([]);
  const [flaggedEvents, setFlaggedEvents] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [cancelMakeupModalOpen, setCancelMakeupModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [makeupDocModalOpen, setMakeupDocModalOpen] = useState(false);
  const [startTimesModalOpen, setStartTimesModalOpen] = useState(false);
  const [modalError, setModalError] = useState("");

  // Filter states
  const [courseNameFilter, setCourseNameFilter] = useState("");
  const [courseCodeFilter, setCourseCodeFilter] = useState("");
  const [courseTypeFilter, setCourseTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    const fetchAllEvents = async () => {
      try {
        const token = getCookie('_auth');
        const response = await Axios.get(
          `${process.env.REACT_APP_URL}/api/v1/makeup`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setAllEvents(response.data);
        const flaggedEvents = await Axios.get(
          `${process.env.REACT_APP_URL}/api/v1/flagged-makeups`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setFlaggedEvents(flaggedEvents.data);
      } catch (error) {
        console.error("Error fetching all events:", error);
      }
    };
    fetchAllEvents();
  }, []);

  const handleCancelMakeup = async () => {
    const token = getCookie('_auth');
    const response = await Axios.delete(`${process.env.REACT_APP_URL}/api/v1/makeup/${selectedRow?.original.id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status === 200) {
      setAllEvents(allEvents.filter((event) => event.id !== selectedRow?.original.id));
      setFlaggedEvents(flaggedEvents.filter((event) => event.id !== selectedRow?.original.id));
      setCancelMakeupModalOpen(false);
    } else {
      setModalError("Failed to delete makeup. Please try again.");
    }
  };

  const renderCourseType = (isPrivate) => {
    if (isPrivate) {
      return "1:1";
    } else {
      return "Group";
    }
  };
  const renderStatus = (status) => {
    switch (status) {
      case 1:
        return "Pending Teacher";
      case 2:
        return "Pending Teacher";
      case 3:
        return "Pending Parent";
      case 4:
        return "Approved";
      default:
        return "Flagged";
    }
  };

  const showDate = (dateString) => {
    if (!dateString) return "Invalid Date";
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      });
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return "Invalid Date";
    }
  };

  const showTime = (dateString) => {
    if (!dateString) return "Invalid Date";
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
      });
    } catch (error) {
      console.error("Error parsing date:", dateString, error);
      return "Invalid Date";
    }
  };

  const getOrdinal = (num) => {
    switch (num) {
        case 1:
            return "1st";
        case 2:
            return "2nd";
        case 3:
            return "3rd";
        default:
            return `${num}th`;
    }
  }

  // Filter the data based on current filters
  const filteredData = useMemo(() => {
    const filtered = allEvents.filter((event) => {
      const courseTitle =
        event.event?.course?.course_title?.toLowerCase() || "";
      const courseCode = event.event?.course?.code?.toLowerCase() || "";
      const isPrivate = event.event?.course?.is_private_course;
      const courseType = isPrivate ? "1:1" : "Group";
      const status = event.status;
      const missedDate = event.event?.start;

      // Exclude approved events (status === 4)
      if (status === 4) {
        return false;
      }

      // Course name filter
      if (
        courseNameFilter &&
        !courseTitle.includes(courseNameFilter.toLowerCase())
      ) {
        return false;
      }

      // Course code filter
      if (
        courseCodeFilter &&
        !courseCode.includes(courseCodeFilter.toLowerCase())
      ) {
        return false;
      }

      // Course type filter
      if (courseTypeFilter && courseType !== courseTypeFilter) {
        return false;
      }

      // Status filter
      if (statusFilter !== "") {
        const filterStatus = parseInt(statusFilter);
        if (filterStatus === 1) {
          // Treat both status 1 and 2 as "Pending Teacher"
          if (status !== 1 && status !== 2) {
            return false;
          }
        } else if (status !== filterStatus) {
          return false;
        }
      }

      // Date range filter
      if (dateRangeFilter.startDate || dateRangeFilter.endDate) {
        const eventDate = DateTime.fromISO(missedDate);
        if (dateRangeFilter.startDate && dateRangeFilter.endDate) {
          const startDate = DateTime.fromISO(dateRangeFilter.startDate);
          const endDate = DateTime.fromISO(dateRangeFilter.endDate);
          // If start and end are the same day, include all events on that day
          if (startDate.hasSame(endDate, 'day')) {
            if (!eventDate.hasSame(startDate, 'day')) {
              return false;
            }
          } else {
            if (eventDate < startDate || eventDate > endDate) {
              return false;
            }
          }
        } else if (dateRangeFilter.startDate) {
          const startDate = DateTime.fromISO(dateRangeFilter.startDate);
          if (eventDate < startDate) {
            return false;
          }
        } else if (dateRangeFilter.endDate) {
          const endDate = DateTime.fromISO(dateRangeFilter.endDate);
          if (eventDate > endDate) {
            return false;
          }
        }
      }

      return true;
    });

    // Sort by createdAt in descending order (latest first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB - dateA; // Descending order
    });
  }, [
    allEvents,
    courseNameFilter,
    courseCodeFilter,
    courseTypeFilter,
    statusFilter,
    dateRangeFilter,
  ]);

  // Sort flagged events by createdAt in descending order (latest first)
  const sortedFlaggedEvents = useMemo(() => {
    return flaggedEvents.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB - dateA; // Descending order
    });
  }, [flaggedEvents]);

  // Clear all filters
  const clearFilters = () => {
    setCourseNameFilter("");
    setCourseCodeFilter("");
    setCourseTypeFilter("");
    setStatusFilter("");
    setDateRangeFilter({ startDate: "", endDate: "" });
  };

  const columns = useMemo(
    () => [
      {
        header: "Course",
        accessorKey: "event.course.course_title",
        cell: ({ getValue, row }) => {
          const courseTitle = getValue();
          return (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap:2, alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: colors.primary[400] }}>
                  {courseTitle}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.primary[400] }}>
                  {row.original.event.course.code}
                </Typography>
              </Box>
              <Box>
                {row.original.is_manual && (
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: '#f8d9ed',
                      color: colors.primary[400],
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      border: `1px solid #ececec`,
                    }}
                  >
                    Manual
                  </Box>
                )}
              </Box>
            </Box>
          );
        },
      },
      {
        header: "Course Type",
        accessorKey: "event.course.is_private_course",
        cell: ({ getValue }) => {
          const isPrivate = getValue();
          return (
            <Box
              sx={{
                display: 'inline-block',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                bgcolor: isPrivate ? '#e6e0fa' : '#e0f7fa',
                color: colors.primary[400],
                fontWeight: 600,
                fontSize: '0.95em',
                minWidth: 48,
                textAlign: 'center',
                border: `1px solid #ececec`,
              }}
            >
              {renderCourseType(isPrivate)}
            </Box>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }) => {
          const status = getValue();
          let bg, text;
         if (status === 1 || status === 2) { bg = '#fff9e6'; text = colors.primary[400]; }
          else if (status === 3) { bg = '#f3e6fa'; text = colors.primary[400]; }
          else if (status === 4) { bg = '#e6fae6'; text = colors.primary[400]; }
          else { bg = '#FF7074'; text = colors.primary[400]; }
          return (
            <Box
              sx={{
                display: 'inline-block',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                bgcolor: bg,
                color: text,
                fontWeight: 600,
                fontSize: '0.95em',
                minWidth: 64,
                textAlign: 'center',
                border: `1px solid #ececec`,
              }}
            >
              {renderStatus(status)}
            </Box>
          );
        },
      },
      {
        header: "Missed Date",
        accessorKey: "event.start",
        cell: ({ getValue }) => {
          const date = getValue();
          return (
            <Typography variant="body1" sx={{ fontWeight: 500, color: colors.primary[400] }}>
              {DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED)}
            </Typography>
          );
        },
      },
      // Actions column removed
    ], [colors]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  // Calculate statistics from all events (including status 4)
  const allStatusCounts = useMemo(() => {
    return allEvents.reduce((acc, event) => {
      acc[event.status] = (acc[event.status] || 0) + 1;
      return acc;
    }, {});
  }, [allEvents]);

  // Calculate statistics from filtered data (excluding status 4)
  const statusCounts = filteredData.reduce((acc, event) => {
    acc[event.status] = (acc[event.status] || 0) + 1;
    return acc;
  }, {});

  // Calculate flagged events statistics
  const totalFlaggedEvents = sortedFlaggedEvents.length;

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f6fa', minHeight: "100vh" }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography 
            variant="h2" 
            sx={{ 
                fontWeight: "bold",
                color: colors.primary[400],
                mb: 0,
            }}
            >
            Makeup Scheduling Dashboard
            </Typography>
            <IconButton
                onClick={() => setMakeupDocModalOpen(true)}
                sx={{
                    ml: 0.5,
                    color: colors.primary[400],
                    background: 'transparent',
                    '&:hover': {
                        background: colors.primary[50],
                    },
                }}
                size="small"
            >
                <HelpOutlineIcon />
            </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setStartTimesModalOpen(true)}
            sx={{
              px: 2,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: '1rem',
              borderWidth: 2,
              '&:hover': {
                transform: 'translateY(-2px)',
                transition: 'transform 0.2s',
                borderWidth: 2
              }
            }}
          >
            Manage Start Times
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setManualModalOpen(true)}
            sx={{
              px: 2,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: '1rem',
              '&:hover': {
                transform: 'translateY(-2px)',
                transition: 'transform 0.2s'
              }
            }}
          >
            + Manual Makeup
          </Button>
        </Box>
        <ManualModal open={manualModalOpen} onClose={() => setManualModalOpen(false)} />
        <MakeupStartTimesModal open={startTimesModalOpen} onClose={() => setStartTimesModalOpen(false)} />
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 1, border: `1px solid ${colors.grey[300]}` }}>
            <Avatar sx={{ bgcolor: colors.redAccent[500], width: 40, height: 40, mb: 1 }}>
              <AccessAlarmsIcon />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: colors.primary[400] }}>{totalFlaggedEvents}</Typography>
            <Typography variant="body2" sx={{ color: colors.primary[400] }}>Flagged Requests</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 1, border: `1px solid ${colors.grey[300]}` }}>
            <Avatar sx={{ bgcolor: "#000000", width: 40, height: 40, mb: 1 }}>
              <AccessAlarmsIcon />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: colors.primary[400] }}>{(statusCounts[1] || 0) + (statusCounts[2] || 0)}</Typography>
            <Typography variant="body2" sx={{ color: colors.primary[400] }}>Pending Teacher</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 1, border: `1px solid ${colors.grey[300]}` }}>
            <Avatar sx={{ bgcolor: colors.blueAccent[500], width: 40, height: 40, mb: 1 }}>
                <AccessAlarmsIcon />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: colors.primary[400] }}>{statusCounts[3] || 0}</Typography>
            <Typography variant="body2" sx={{ color: colors.primary[400] }}>Pending Parent</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 1, border: `1px solid ${colors.grey[300]}` }}>
            <Avatar sx={{ bgcolor: colors.greenAccent[300], width: 40, height: 40, mb: 1 }}>
              <AccessAlarmsIcon />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: colors.primary[400] }}>{allStatusCounts[4] || 0}</Typography>
            <Typography variant="body2" sx={{ color: colors.primary[400] }}>Approved</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Filter Section */}
      <Box sx={{ width: "100%", mb: 2, bgcolor: '#f5f6fa', borderRadius: 2, p: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, pl: 1 }}>
          <FilterListIcon sx={{ color: colors.blueAccent[500] }} />
          <Typography variant="h6" sx={{ fontWeight: "bold", color: colors.primary[400] }}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Course Name"
              value={courseNameFilter}
              onChange={(e) => setCourseNameFilter(e.target.value)}
              size="small"
              sx={{
                bgcolor: 'white',
                "& .MuiOutlinedInput-root": {
                  bgcolor: 'white',
                  "& fieldset": { borderColor: colors.grey[300] },
                },
                "& .MuiInputLabel-root": { color: colors.primary[400] },
                "& .MuiInputBase-input": { color: colors.primary[400] },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="Course Code"
              value={courseCodeFilter}
              onChange={(e) => setCourseCodeFilter(e.target.value)}
              size="small"
              sx={{
                bgcolor: 'white',
                "& .MuiOutlinedInput-root": {
                  bgcolor: 'white',
                  "& fieldset": { borderColor: colors.grey[300] },
                },
                "& .MuiInputLabel-root": { color: colors.primary[400] },
                "& .MuiInputBase-input": { color: colors.primary[400] },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: colors.primary[400] }}>Course Type</InputLabel>
              <Select
                value={courseTypeFilter}
                label="Course Type"
                onChange={(e) => setCourseTypeFilter(e.target.value)}
                sx={{
                  bgcolor: 'white',
                  color: colors.primary[400],
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[300] },
                  "& .MuiSelect-icon": { color: colors.primary[400] },
                }}
              >
                <SelectMenuItem value="">All</SelectMenuItem>
                <SelectMenuItem value="1:1">1:1</SelectMenuItem>
                <SelectMenuItem value="Group">Group</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: colors.primary[400] }}>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{
                  bgcolor: 'white',
                  color: colors.primary[400],
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: colors.grey[300] },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: colors.blueAccent[500] },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: colors.blueAccent[500] },
                  "& .MuiSelect-icon": { color: colors.primary[400] },
                }}
              >
                <SelectMenuItem value="">All</SelectMenuItem>
                <SelectMenuItem value={0}>Flagged</SelectMenuItem>
                <SelectMenuItem value={1}>Pending Teacher</SelectMenuItem>
                <SelectMenuItem value={3}>Pending Parent</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="From Date"
                type="date"
                value={dateRangeFilter.startDate}
                onChange={(e) =>
                  setDateRangeFilter((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{
                  flex: 1,
                  bgcolor: 'white',
                  "& .MuiOutlinedInput-root": {
                    bgcolor: 'white',
                    "& fieldset": { borderColor: colors.grey[300] },
                  },
                  "& .MuiInputLabel-root": { color: colors.primary[400] },
                  "& .MuiInputBase-input": { color: colors.primary[400] },
                }}
              />
              <TextField
                label="To Date"
                type="date"
                value={dateRangeFilter.endDate}
                onChange={(e) =>
                  setDateRangeFilter((prev) => ({
                    ...prev,
                    endDate: e.target.value,
                  }))
                }
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{
                  flex: 1,
                  bgcolor: 'white',
                  "& .MuiOutlinedInput-root": {
                    bgcolor: 'white',
                    "& fieldset": { borderColor: colors.grey[300] },
                  },
                  "& .MuiInputLabel-root": { color: colors.primary[400] },
                  "& .MuiInputBase-input": { color: colors.primary[400] },
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Button variant="outlined" onClick={clearFilters} sx={{ mt: 1, borderColor: colors.blueAccent[500], color: colors.blueAccent[500]}}>
              Clear All Filters
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Flagged Events Table */}
      {sortedFlaggedEvents.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ width: '100%', bgcolor: 'white', borderRadius: 2, p: 0, boxShadow: 0, border: `2px solid ${colors.redAccent[500]}`, borderStyle: 'dashed' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} sx={{ bgcolor: colors.redAccent[50] }}>
                      {headerGroup.headers.map((header) => (
                        <TableCell key={header.id} sx={{ fontSize: "1rem", color: colors.redAccent[500], background: colors.redAccent[50], fontWeight: 'bold', borderBottom: `2px solid ${colors.redAccent[500]}` }}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {sortedFlaggedEvents.map((event, index) => [
                    <TableRow
                      key={`flagged-${index}`}
                      sx={{ bgcolor: 'white', cursor: 'pointer' }}
                      onClick={() => setExpandedRowId(expandedRowId === `flagged-${index}` ? null : `flagged-${index}`)}
                    >
                      <TableCell sx={{ fontSize: "1rem", color: colors.primary[400], borderBottom: `1px solid #ececec` }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.primary[400] }}>
                            {event.event?.course?.course_title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: colors.primary[400] }}>
                            {event.event?.course?.code}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: "1rem", color: colors.primary[400], borderBottom: `1px solid #ececec` }}>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: event.event?.course?.is_private_course ? '#e6e0fa' : '#e0f7fa',
                            color: colors.primary[400],
                            fontWeight: 600,
                            fontSize: '0.95em',
                            minWidth: 48,
                            textAlign: 'center',
                            border: `1px solid #ececec`,
                          }}
                        >
                          {renderCourseType(event.event?.course?.is_private_course)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: "1rem", color: colors.primary[400], borderBottom: `1px solid #ececec` }}>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: (event.status === 1 || event.status === 2) ? '#fff9e6' : 
                                   event.status === 3 ? '#f3e6fa' : 
                                   event.status === 4 ? '#e6fae6' : '#FF7074',
                            color: colors.primary[400],
                            fontWeight: 600,
                            fontSize: '0.95em',
                            minWidth: 64,
                            textAlign: 'center',
                            border: `1px solid #ececec`,
                          }}
                        >
                          {renderStatus(event.status)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: "1rem", color: colors.primary[400], borderBottom: `1px solid #ececec` }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: colors.primary[400] }}>
                          {DateTime.fromISO(event.event?.start).toLocaleString(DateTime.DATE_MED)}
                        </Typography>
                      </TableCell>
                    </TableRow>,
                    expandedRowId === `flagged-${index}` && (
                      <TableRow key={`flagged-${index}-expanded`}>
                        <TableCell colSpan={4} sx={{ bgcolor: '#f9f9fb', p: 3, borderBottom: `1px solid #ececec` }}>
                          {/* Progress content for flagged events */}
                          <Box>
                            {/* Course Info */}
                            <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f6fa", borderRadius: 1 }}>
                              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1, color: colors.primary[400] }}>
                                {event.event?.course?.course_title} (
                                {event.event?.course?.code})
                              </Typography>
                              <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                Missed Date: {DateTime.fromISO(event.event?.start).toLocaleString(DateTime.DATE_MED)}
                              </Typography>
                            </Box>
                            {/* Progress Stepper */}
                            <Stepper orientation="vertical" sx={{ mt: 2 }}>
                              {/* Step 1: Makeup Request Created */}
                              <Step
                                active={event.status >= 0}
                                completed={event.status > 0}
                              >
                                <StepLabel
                                  StepIconComponent={
                                    event.status > 0
                                      ? CheckCircleIcon
                                      : RadioButtonUncheckedIcon
                                  }
                                  sx={{
                                    "& .MuiStepLabel-iconContainer": {
                                      color:
                                        event.status > 0
                                          ? "success.main"
                                          : "grey.400",
                                    },
                                  }}
                                >
                                  <Typography variant="h4" sx={{ fontWeight: "bold", color: colors.primary[400] }}>
                                    Makeup Request Created
                                  </Typography>
                                </StepLabel>
                                <StepContent>
                                  <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                    Initial makeup request has been created for the missed class on {DateTime.fromISO(event.createdAt).toLocaleString(DateTime.DATETIME_MED)}.
                                  </Typography>
                                </StepContent>
                              </Step>
                              {/* Step 2: Email Sent to Teacher */}
                              <Step
                                active={event.status >= 0}
                                completed={event.status > 0}
                              >
                                <StepLabel
                                  StepIconComponent={
                                    event.status > 0
                                      ? CheckCircleIcon
                                      : RadioButtonUncheckedIcon
                                  }
                                  sx={{
                                    "& .MuiStepLabel-iconContainer": {
                                      color:
                                        event.status > 0
                                          ? "success.main"
                                          : "grey.400",
                                    },
                                  }}
                                >
                                  <Typography variant="h4" sx={{ fontWeight: "bold", color: colors.primary[400] }}>
                                    Email Sent to Teacher
                                  </Typography>
                                </StepLabel>
                                <StepContent>
                                  <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                    Notification email has been sent to the teacher about the makeup request on {DateTime.fromISO(event.teacher_sent_at).toLocaleString(DateTime.DATETIME_MED)}.
                                  </Typography>
                                </StepContent>
                              </Step>
                              {/* Step 3: Awaiting Teacher Approval */}
                              <Step
                                active={event.status >= 1}
                                completed={event.status > 2}
                              >
                                <StepLabel
                                  StepIconComponent={
                                    event.status > 2
                                      ? CheckCircleIcon
                                      : RadioButtonUncheckedIcon
                                  }
                                  sx={{
                                    "& .MuiStepLabel-iconContainer": {
                                      color:
                                        event.status > 2
                                          ? "success.main"
                                          : event.status >= 1
                                          ? "warning.main"
                                          : "grey.400",
                                    },
                                  }}
                                >
                                  <Typography variant="h4" sx={{ fontWeight: "bold", color: colors.primary[400] }}>
                                    Awaiting Teacher Approval
                                  </Typography>
                                </StepLabel>
                                <StepContent>
                                  <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                    {event.status === 1 || event.status === 2
                                      ? `Teacher is reviewing and selecting available time slots for the makeup class. (${event.teacher_redo_count} redo)`
                                      : "Teacher has selected available time slots and submitted for parent approval."}
                                  </Typography>
                                  <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                    {(event.status >= 3 && event.selected_windows) && (
                                      <>
                                        <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold', color: colors.primary[400] }}>
                                          Selected Windows:
                                        </Typography>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, ml: 2 }}>
                                          {Array.isArray(event.selected_windows) &&
                                            event.selected_windows.length > 0 &&
                                            Array.from({ length: Math.floor(event.selected_windows.length / 2) }).map((_, pairIndex) => {
                                              const pair = event.selected_windows.slice(pairIndex * 2, pairIndex * 2 + 2);
                                              return (
                                                <Typography key={pairIndex} variant="body1" sx={{ color: colors.primary[400] }}>
                                                  {`${getOrdinal(pairIndex + 1)} try: ${showDate(pair[0].start)} - ${showTime(pair[0].end)}`}
                                                  {pair[1] && `, ${showDate(pair[1].start)} - ${showTime(pair[1].end)}`}
                                                </Typography>
                                              );
                                            })}
                                        </Box>
                                      </>
                                    )}
                                  </Typography>
                                </StepContent>
                              </Step>
                              {/* Step 4: Awaiting Parent Approval */}
                              <Step
                                active={event.status >= 3}
                                completed={event.status > 3}
                              >
                                <StepLabel
                                  StepIconComponent={
                                    event.status > 3
                                      ? CheckCircleIcon
                                      : RadioButtonUncheckedIcon
                                  }
                                  sx={{
                                    "& .MuiStepLabel-iconContainer": {
                                      color:
                                        event.status > 3
                                          ? "success.main"
                                          : event.status === 3
                                          ? "warning.main"
                                          : "grey.400",
                                    },
                                  }}
                                >
                                  <Typography variant="h4" sx={{ fontWeight: "bold", color: colors.primary[400] }}>
                                    Awaiting Parent Approval ({event.parent_redo_count}/2 Redo)
                                  </Typography>
                                </StepLabel>
                                <StepContent>
                                  <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                    Parent has received the proposed makeup schedule on {DateTime.fromISO(event.parent_sent_at).toLocaleString(DateTime.DATETIME_MED)}.
                                    {event.status === 3
                                      ? ` Parent is reviewing the proposed makeup schedule and needs to confirm.`
                                      : " Parent has approved the makeup schedule."}
                                  </Typography>
                                </StepContent>
                              </Step>
                              {/* Step 5: Approved */}
                              <Step
                                active={event.status >= 4}
                                completed={event.status === 4}
                              >
                                <StepLabel
                                  StepIconComponent={
                                    event.status === 4
                                      ? CheckCircleIcon
                                      : RadioButtonUncheckedIcon
                                  }
                                  sx={{
                                    "& .MuiStepLabel-iconContainer": {
                                      color:
                                        event.status === 4
                                          ? "success.main"
                                          : "grey.400",
                                    },
                                  }}
                                >
                                  <Typography variant="h4" sx={{ fontWeight: "bold", color: colors.primary[400] }}>
                                    Approved
                                  </Typography>
                                </StepLabel>
                                <StepContent>
                                  <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                    {event.status === 4 && event.final_window && (
                                      <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold', color: colors.primary[400] }}>
                                        Finalized Class: {showDate(event.final_window.start)} - {showDate(event.final_window.end)}
                                      </Typography>
                                    )}
                                  </Typography>
                                </StepContent>
                              </Step>
                            </Stepper>
                            {/* Delete Makeup Button for Flagged Events */}
                            <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
                              <Button
                                variant="contained"
                                color="error"
                                onClick={() => {
                                  setSelectedRow({ original: event });
                                  setCancelMakeupModalOpen(true);
                                }}
                                sx={{
                                  px: 3,
                                  py: 1,
                                  fontWeight: 'bold',
                                  textTransform: 'none',
                                  fontSize: '1rem',
                                }}
                              >
                                Delete Makeup
                              </Button>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  ])}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}

      {/* Divider between filter and table */}
      <Box sx={{ height: 8 }} />
      {/* Events Table */}
      <Box sx={{ width: '100%', bgcolor: 'white', borderRadius: 2, p: 0, boxShadow: 0 }}>
        <TableContainer>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} sx={{ bgcolor: 'white' }}>
                  {headerGroup.headers.map((header) => (
                    <TableCell key={header.id} sx={{ fontSize: "1rem", color: colors.primary[400], background: 'white', fontWeight: 'bold', borderBottom: `1px solid #ececec` }}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => [
                <TableRow
                  key={row.id}
                  sx={{ bgcolor: 'white', cursor: 'pointer' }}
                  onClick={() => setExpandedRowId(expandedRowId === row.id ? null : row.id)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} sx={{ fontSize: "1rem", color: colors.primary[400], borderBottom: `1px solid #ececec` }}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>,
                expandedRowId === row.id && (
                  <TableRow key={row.id + '-expanded'}>
                    <TableCell colSpan={columns.length} sx={{ bgcolor: '#f9f9fb', p: 3, borderBottom: `1px solid #ececec` }}>
                      {/* Progress content (from View Progress Modal) */}
                      <Box>
                        {/* Course Info */}
                        <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f6fa", borderRadius: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1, color: colors.primary[400] }}>
                            {row.original.event?.course?.course_title} (
                            {row.original.event?.course?.code})
                          </Typography>
                          <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                            Missed Date: {DateTime.fromISO(row.original.event?.start).toLocaleString(DateTime.DATE_MED)}
                          </Typography>
                        </Box>
                        {/* Progress Stepper */}
                        <Stepper orientation="vertical" sx={{ mt: 2 }}>
                          {/* Step 1: Makeup Request Created */}
                          <Step
                            active={row.original.status >= 0}
                            completed={row.original.status > 0}
                          >
                            <StepLabel
                              StepIconComponent={
                                row.original.status > 0
                                  ? CheckCircleIcon
                                  : RadioButtonUncheckedIcon
                              }
                              sx={{
                                "& .MuiStepLabel-iconContainer": {
                                  color:
                                    row.original.status > 0
                                      ? "success.main"
                                      : "grey.400",
                                },
                              }}
                            >
                              <Typography variant="h4" sx={{ fontWeight: "bold", color: colors.primary[400] }}>
                                Makeup Request Created
                              </Typography>
                            </StepLabel>
                            <StepContent>
                              <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                Initial makeup request has been created for the missed class on {DateTime.fromISO(row.original.createdAt).toLocaleString(DateTime.DATETIME_MED)}.
                              </Typography>
                            </StepContent>
                          </Step>
                          {/* Step 2: Email Sent to Teacher */}
                          <Step
                            active={row.original.status >= 0}
                            completed={row.original.status > 0}
                          >
                            <StepLabel
                              StepIconComponent={
                                row.original.status > 0
                                  ? CheckCircleIcon
                                  : RadioButtonUncheckedIcon
                              }
                              sx={{
                                "& .MuiStepLabel-iconContainer": {
                                  color:
                                    row.original.status > 0
                                      ? "success.main"
                                      : "grey.400",
                                },
                              }}
                            >
                              <Typography variant="h4" sx={{ fontWeight: "bold", color: colors.primary[400] }}>
                                Email Sent to Teacher
                              </Typography>
                            </StepLabel>
                            <StepContent>
                              <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                Notification email has been sent to the teacher about the makeup request on {DateTime.fromISO(row.original.teacher_sent_at).toLocaleString(DateTime.DATETIME_MED)}.
                              </Typography>
                            </StepContent>
                          </Step>
                          {/* Step 3: Awaiting Teacher Approval */}
                          <Step
                            active={row.original.status >= 1}
                            completed={row.original.status > 2}
                          >
                            <StepLabel
                              StepIconComponent={
                                row.original.status > 2
                                  ? CheckCircleIcon
                                  : RadioButtonUncheckedIcon
                              }
                              sx={{
                                "& .MuiStepLabel-iconContainer": {
                                  color:
                                    row.original.status > 2
                                      ? "success.main"
                                      : row.original.status >= 1
                                      ? "warning.main"
                                      : "grey.400",
                                },
                              }}
                            >
                              <Typography variant="h4" sx={{ fontWeight: "bold", color: colors.primary[400] }}>
                                Awaiting Teacher Approval
                              </Typography>
                            </StepLabel>
                            <StepContent>
                              <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                {row.original.status === 1 || row.original.status === 2
                                  ? `Teacher is reviewing and selecting available time slots for the makeup class. (${row.original.teacher_redo_count} redo)`
                                  : "Teacher has selected available time slots and submitted for parent approval."}
                              </Typography>
                              <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                {(row.original.status >= 3 && row.original.selected_windows) && (
                                  <>
                                    <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold', color: colors.primary[400] }}>
                                      Selected Windows:
                                    </Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, ml: 2 }}>
                                      {Array.isArray(row.original.selected_windows) &&
                                            row.original.selected_windows.length > 0 &&
                                            Array.from({ length: Math.floor(row.original.selected_windows.length / 2) }).map((_, pairIndex) => {
                                              const pair = row.original.selected_windows.slice(pairIndex * 2, pairIndex * 2 + 2);
                                              return (
                                                <Typography key={pairIndex} variant="body1" sx={{ color: colors.primary[400] }}>
                                                  {`${getOrdinal(pairIndex + 1)} try: ${showDate(pair[0].start)} - ${showTime(pair[0].end)}`}
                                                  {pair[1] && `, ${showDate(pair[1].start)} - ${showTime(pair[1].end)}`}
                                                </Typography>
                                              );
                                            })}
                                    </Box>
                                  </>
                                )}
                              </Typography>
                            </StepContent>
                          </Step>
                          {/* Step 4: Awaiting Parent Approval */}
                          <Step
                            active={row.original.status >= 3}
                            completed={row.original.status > 3}
                          >
                            <StepLabel
                              StepIconComponent={
                                row.original.status > 3
                                  ? CheckCircleIcon
                                  : RadioButtonUncheckedIcon
                              }
                              sx={{
                                "& .MuiStepLabel-iconContainer": {
                                  color:
                                    row.original.status > 3
                                      ? "success.main"
                                      : row.original.status === 3
                                      ? "warning.main"
                                      : "grey.400",
                                },
                              }}
                            >
                              <Typography variant="h4" sx={{ fontWeight: "bold", color: colors.primary[400] }}>
                                Awaiting Parent Approval ({row.original.parent_redo_count}/2 Redo)
                              </Typography>
                            </StepLabel>
                            <StepContent>
                              <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                Parent has received the makeup schedule on {DateTime.fromISO(row.original.parent_sent_at).toLocaleString(DateTime.DATETIME_MED)}. <br />
                                {row.original.status === 3
                                  ? `Parent is reviewing the proposed makeup schedule and needs to confirm.`
                                  : "Parent has approved the makeup schedule."}
                              </Typography>
                            </StepContent>
                          </Step>
                          {/* Step 5: Approved */}
                          <Step
                            active={row.original.status >= 4}
                            completed={row.original.status === 4}
                          >
                            <StepLabel
                              StepIconComponent={
                                row.original.status === 4
                                  ? CheckCircleIcon
                                  : RadioButtonUncheckedIcon
                              }
                              sx={{
                                "& .MuiStepLabel-iconContainer": {
                                  color:
                                    row.original.status === 4
                                      ? "success.main"
                                      : "grey.400",
                                },
                              }}
                            >
                              <Typography variant="h4" sx={{ fontWeight: "bold", color: colors.primary[400] }}>
                                Approved
                              </Typography>
                            </StepLabel>
                            <StepContent>
                              <Typography variant="body1" sx={{ color: colors.primary[400] }}>
                                {row.original.status === 4 && row.original.final_window && (
                                  <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold', color: colors.primary[400] }}>
                                    Finalized Class: {showDate(row.original.final_window.start)} - {showDate(row.original.final_window.end)}
                                  </Typography>
                                )}
                              </Typography>
                            </StepContent>
                          </Step>
                        </Stepper>
                        {/* Delete Makeup Button for Regular Events */}
                        <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => {
                              setSelectedRow({ original: row.original });
                              setCancelMakeupModalOpen(true);
                            }}
                            sx={{
                              px: 3,
                              py: 1,
                              fontWeight: 'bold',
                              textTransform: 'none',
                              fontSize: '1rem',
                            }}
                          >
                            Delete Makeup
                          </Button>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              ])}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Cancel Makeup Modal */}
      <Modal
        open={cancelMakeupModalOpen}
        onClose={() => setCancelMakeupModalOpen(false)}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Fade in={cancelMakeupModalOpen}>
          <Box
            sx={{
              position: "relative",
              maxWidth: 600,
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              outline: "none",
            }}
          >
            <Typography variant="h4" sx={{ mb: 2, fontWeight: "bold" }}>
              Delete Makeup
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this makeup? <br />
              <strong>You need to un-cancel the event in weekly manually.</strong><br />
              This action cannot be undone.
            </Typography>
            {modalError && (
              <Typography variant="body1" sx={{ mb: 2, color: "red" }}>
                {modalError}
              </Typography>
            )}
            <Button
              variant="contained"
              color="error"
              onClick={handleCancelMakeup}
              sx={{ mr: 2 }}
            >
              Confirm
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setCancelMakeupModalOpen(false)}
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
          </Box>
        </Fade>
      </Modal>

      <MakeupDocModal open={makeupDocModalOpen} onClose={() => setMakeupDocModalOpen(false)} />
    </Box>
  );
}
